import { useState, useEffect } from 'react';

/**
 * React hook that detects mobile/narrow screens via CSS media query.
 * Uses matchMedia for instant, resize-responsive detection.
 * Matches phones in portrait (narrow width) OR landscape (touch + short height).
 * Excludes tablets (iPad height ≥ 700px in either orientation).
 * @param {number} breakpoint - Max width in px (default 768)
 * @returns {boolean} true when on a phone-sized device
 */
const useIsMobile = (breakpoint = 768) => {
  // Portrait phones: narrow viewport width
  // Landscape phones: touch-primary device with short viewport height (phones are <500px tall in landscape)
  const query = `(max-width: ${breakpoint}px), ((pointer: coarse) and (max-height: 500px) and (max-width: 1024px))`;
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e) => setIsMobile(e.matches);

    // Modern API
    if (mql.addEventListener) {
      mql.addEventListener('change', handler);
      return () => mql.removeEventListener('change', handler);
    }
    // Safari 13 fallback
    mql.addListener(handler);
    return () => mql.removeListener(handler);
  }, [query]);

  return isMobile;
};

export default useIsMobile;
